import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import ObjectPricingPlanForm from 'src/features/pricingPlan/components/form/ObjectPricingPlanForm';
import { pricingPlanObjectFormSchema, PricingPlanObjectFormSchemaValues } from 'src/features/pricing/sites/utils/formSchema';
import { useGetGatewayPricingPlans } from 'src/features/gateway/api/useGetGatewayPricingPlans';
import { GatewayPricingPlan } from 'src/features/gateway/utils/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useParking } from 'src/providers/ParkingProvider';

interface PricingPlanGatewayFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedGatewayPricingPlan?: GatewayPricingPlan;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: PricingPlanObjectFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function GatewayPricingPlanFormModal<TData>({
    selectedGatewayPricingPlan,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: PricingPlanGatewayFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();

    // We are in edit mode if we have a selected pricing plan
    // otherwise it means we are creating a new pricing plan
    const { data: gatewayPricingPlan } = useGetGatewayPricingPlans({
        planId: selectedGatewayPricingPlan?.id ?? '',
        parkingId,
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedGatewayPricingPlan?.id && isModalOpen },
    });
    const onError = useOnError();

    const defaultValues = React.useMemo(() => gatewayPricingPlan ?? {}, [gatewayPricingPlan]);

    const form = useForm<PricingPlanObjectFormSchemaValues>({
        resolver: zodResolver(pricingPlanObjectFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [gatewayPricingPlan, form, selectedGatewayPricingPlan, defaultValues, isModalOpen]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: PricingPlanObjectFormSchemaValues) => {
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ObjectPricingPlanForm form={form} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedGatewayPricingPlan && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default GatewayPricingPlanFormModal;
